import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, detailText, idr, formatMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Plane,
  Calendar,
  MapPin,
  Clock,
  Wallet,
  CheckSquare,
  FileText,
  Plus,
  Pencil,
  Camera,
  Trash2,
  Sparkles,
  CheckCircle2,
  Building2,
  Utensils,
  Ticket,
  Luggage,
  AlertTriangle,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Compass,
  CreditCard,
  Check,
  X,
  ExternalLink,
  Loader2,
} from "lucide-react";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=1200&q=80";

const BUDGET_CATEGORIES = ["Transportasi", "Hotel", "Makanan", "Aktivitas", "Souvenir", "Emergency"];
const BOOKING_TYPES = ["Flight", "Hotel", "Transport", "Activity"];
const BOOKING_STATUSES = ["Pending", "Paid", "Completed"];
const CHECKLIST_CATEGORIES = ["Before Departure", "During Trip"];
const TRIP_STATUSES = ["Planning", "Booked", "Ready", "Completed"];

export default function Honeymoon() {
  const navigate = useNavigate();
  const location = useLocation();

  // Ambil sub-tab dari URL (/honeymoon/budget -> 'budget')
  const currentTab = location.pathname.split("/")[2] || "overview";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ trip: null, summary: null });

  // Data Module State
  const [budgetData, setBudgetData] = useState({ items: [], categories: [], total_planned: 0, total_actual: 0 });
  const [itineraryData, setItineraryData] = useState({ items: [] });
  const [bookingData, setBookingData] = useState({ bookings: [] });
  const [checklistData, setChecklistData] = useState({ items: [] });

  // Modals
  const [tripModalOpen, setTripModalOpen] = useState(false);
  const [tripForm, setTripForm] = useState({});

  // Budget Modal
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [editingBudgetItem, setEditingBudgetItem] = useState(null);
  const [budgetForm, setBudgetForm] = useState({ category: "Hotel", name: "", planned_amount: 0, actual_amount: 0, description: "", date: "" });

  // Itinerary Modal
  const [itineraryModalOpen, setItineraryModalOpen] = useState(false);
  const [editingItineraryItem, setEditingItineraryItem] = useState(null);
  const [itineraryForm, setItineraryForm] = useState({ date: "", time: "09:00", title: "", location: "", description: "", image: "" });

  // Booking Modal
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [editingBookingItem, setEditingBookingItem] = useState(null);
  const [bookingForm, setBookingForm] = useState({ type: "Hotel", name: "", booking_date: "", status: "Pending", document: "" });

  // Checklist Modal
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const [editingChecklistItem, setEditingChecklistItem] = useState(null);
  const [checklistForm, setChecklistForm] = useState({ title: "", category: "Before Departure" });

  const loadTripSummary = useCallback(async () => {
    try {
      const res = await api.get("/honeymoon/trip");
      setData(res.data);
      if (res.data.trip) {
        setTripForm(res.data.trip);
      }
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal memuat data honeymoon");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBudget = useCallback(async () => {
    try {
      const res = await api.get("/honeymoon/budget");
      setBudgetData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadItinerary = useCallback(async () => {
    try {
      const res = await api.get("/honeymoon/itinerary");
      setItineraryData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadBooking = useCallback(async () => {
    try {
      const res = await api.get("/honeymoon/booking");
      setBookingData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadChecklist = useCallback(async () => {
    try {
      const res = await api.get("/honeymoon/checklist");
      setChecklistData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadTripSummary();
    loadBudget();
    loadItinerary();
    loadBooking();
    loadChecklist();
  }, [loadTripSummary, loadBudget, loadItinerary, loadBooking, loadChecklist]);

  const [uploadingCover, setUploadingCover] = useState(false);

  const handleUploadCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran foto cover maksimal 5 MB");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setUploadingCover(true);
    try {
      await api.post("/honeymoon/cover", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto cover Honeymoon berhasil diperbarui ✨");
      loadTripSummary();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal mengunggah foto cover");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDeleteCover = async () => {
    if (!window.confirm("Hapus foto cover honeymoon?")) return;
    try {
      await api.delete("/honeymoon/cover");
      toast.success("Foto cover dihapus");
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menghapus foto cover");
    }
  };

  // Trip Updates
  const handleSaveTrip = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/honeymoon/trip", tripForm);
      toast.success("Informasi Honeymoon berhasil diperbarui! ✨");
      setTripModalOpen(false);
      loadTripSummary();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal memperbarui trip");
    }
  };

  // Budget Handlers
  const handleSaveBudget = async (e) => {
    e.preventDefault();
    if (!budgetForm.name.trim()) return toast.error("Nama pengeluaran wajib diisi");
    try {
      if (editingBudgetItem) {
        await api.patch(`/honeymoon/budget/${editingBudgetItem.budget_id}`, budgetForm);
        toast.success("Pengeluaran berhasil diperbarui");
      } else {
        await api.post("/honeymoon/budget", budgetForm);
        toast.success("Pengeluaran baru ditambahkan");
      }
      setBudgetModalOpen(false);
      loadBudget();
      loadTripSummary();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menyimpan budget");
    }
  };

  const handleDeleteBudget = async (id) => {
    if (!window.confirm("Hapus item pengeluaran ini?")) return;
    try {
      await api.delete(`/honeymoon/budget/${id}`);
      toast.success("Item pengeluaran dihapus");
      loadBudget();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menghapus item budget");
    }
  };

  // Itinerary Handlers
  const handleSaveItinerary = async (e) => {
    e.preventDefault();
    if (!itineraryForm.title.trim()) return toast.error("Judul aktivitas wajib diisi");
    try {
      if (editingItineraryItem) {
        await api.patch(`/honeymoon/itinerary/${editingItineraryItem.itinerary_id}`, itineraryForm);
        toast.success("Aktivitas berhasil diperbarui");
      } else {
        await api.post("/honeymoon/itinerary", itineraryForm);
        toast.success("Aktivitas baru ditambahkan");
      }
      setItineraryModalOpen(false);
      loadItinerary();
      loadTripSummary();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menyimpan aktivitas");
    }
  };

  const handleDeleteItinerary = async (id) => {
    if (!window.confirm("Hapus aktivitas ini?")) return;
    try {
      await api.delete(`/honeymoon/itinerary/${id}`);
      toast.success("Aktivitas dihapus");
      loadItinerary();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menghapus aktivitas");
    }
  };

  // Booking Handlers
  const handleSaveBooking = async (e) => {
    e.preventDefault();
    if (!bookingForm.name.trim()) return toast.error("Nama booking wajib diisi");
    try {
      if (editingBookingItem) {
        await api.patch(`/honeymoon/booking/${editingBookingItem.booking_id}`, bookingForm);
        toast.success("Booking berhasil diperbarui");
      } else {
        await api.post("/honeymoon/booking", bookingForm);
        toast.success("Booking baru ditambahkan");
      }
      setBookingModalOpen(false);
      loadBooking();
      loadTripSummary();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menyimpan booking");
    }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm("Hapus booking ini?")) return;
    try {
      await api.delete(`/honeymoon/booking/${id}`);
      toast.success("Booking dihapus");
      loadBooking();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menghapus booking");
    }
  };

  // Checklist Handlers
  const handleToggleChecklist = async (item) => {
    try {
      await api.patch(`/honeymoon/checklist/${item.checklist_id}`, { completed: !item.completed });
      loadChecklist();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal memperbarui checklist");
    }
  };

  const handleSaveChecklist = async (e) => {
    e.preventDefault();
    if (!checklistForm.title.trim()) return toast.error("Judul item checklist wajib diisi");
    try {
      if (editingChecklistItem) {
        await api.patch(`/honeymoon/checklist/${editingChecklistItem.checklist_id}`, checklistForm);
        toast.success("Checklist diperbarui");
      } else {
        await api.post("/honeymoon/checklist", checklistForm);
        toast.success("Checklist ditambahkan");
      }
      setChecklistModalOpen(false);
      loadChecklist();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menyimpan checklist");
    }
  };

  const handleDeleteChecklist = async (id) => {
    try {
      await api.delete(`/honeymoon/checklist/${id}`);
      loadChecklist();
      loadTripSummary();
    } catch (err) {
      toast.error("Gagal menghapus item checklist");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center">
        <div className="text-center text-muted-foreground animate-pulse">
          <Plane className="h-8 w-8 mx-auto mb-2 text-primary" />
          <p className="text-sm">Memuat Honeymoon Planner...</p>
        </div>
      </div>
    );
  }

  const trip = data.trip || {};
  const summary = data.summary || {};

  // Progress Percentages
  const budgetPct = summary.total_budget > 0 ? Math.min(100, Math.round((summary.budget_used / summary.total_budget) * 100)) : 0;
  const bookingPct = summary.booking_count > 0 ? Math.min(100, Math.round((summary.booking_completed / summary.booking_count) * 100)) : 0;
  const checklistPct = summary.checklist_count > 0 ? Math.min(100, Math.round((summary.checklist_completed / summary.checklist_count) * 100)) : 0;

  // Grouped Itinerary items by Date / Day
  const groupedItinerary = {};
  (itineraryData.items || []).forEach((item) => {
    const d = item.date || "Unscheduled";
    if (!groupedItinerary[d]) groupedItinerary[d] = [];
    groupedItinerary[d].push(item);
  });

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="honeymoon-page">
      {/* HEADER BANNER CARD */}
      <Card className="rounded-3xl overflow-hidden border border-border/80 shadow-md relative bg-card">
        <div className="h-48 md:h-64 w-full relative overflow-hidden bg-muted">
          <img
            src={formatMediaUrl(trip.cover_image) || DEFAULT_COVER}
            alt={trip.destination}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Badge className="bg-white/90 text-foreground dark:bg-black/80 dark:text-white backdrop-blur-md px-3 py-1 font-semibold text-xs rounded-full shadow-sm">
              {trip.status || "Planning"}
            </Badge>
            <label className="cursor-pointer inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/90 text-foreground hover:bg-white text-xs font-semibold shadow-sm backdrop-blur-md transition-colors">
              {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
              <span>{uploadingCover ? "Mengunggah..." : "Ganti Cover"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleUploadCoverFile} disabled={uploadingCover} />
            </label>
            <Button
              size="icon"
              variant="secondary"
              onClick={() => setTripModalOpen(true)}
              className="h-8 w-8 rounded-full bg-white/90 text-foreground hover:bg-white shadow-sm"
              title="Edit Honeymoon Trip"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="absolute bottom-4 left-4 right-4 md:left-8 md:right-8 text-white space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/80 text-white backdrop-blur-md text-xs font-semibold">
              <Clock className="h-3.5 w-3.5" />
              {summary.countdown_days !== null && summary.countdown_days >= 0
                ? `Honeymoon starts in ${summary.countdown_days} days`
                : summary.countdown_days !== null
                ? "Honeymoon in progress / completed"
                : "Honeymoon Planning"}
            </div>

            <h1 className="font-serif text-2xl md:text-4xl font-bold truncate">
              {trip.title || "Bali Romantic Honeymoon"}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-white/90 font-medium">
              <span className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-amber-300" /> {trip.destination || "Bali, Indonesia"}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-amber-300" />
                {trip.start_date && trip.end_date
                  ? `${trip.start_date} - ${trip.end_date}`
                  : "Tanggal belum diatur"}
              </span>
              <span className="flex items-center gap-1.5">
                <Luggage className="h-4 w-4 text-amber-300" /> {trip.duration || "5 Days 4 Nights"}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* 4 CARD STATISTICS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Budget */}
        <Card className="p-5 rounded-2xl border border-border/70 shadow-sm space-y-3 bg-card hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Budget</span>
            <div className="h-8 w-8 rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400 flex items-center justify-center">
              <Wallet className="h-4 w-4" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl font-bold text-foreground">Rp {idr(summary.total_budget || 0).replace("Rp ", "")}</div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Used: Rp {idr(summary.budget_used || 0).replace("Rp ", "")}</span>
              <span className="font-medium text-emerald-600">Sisa: Rp {idr(summary.budget_remaining || 0).replace("Rp ", "")}</span>
            </div>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${budgetPct}%` }} />
          </div>
        </Card>

        {/* Card 2: Itinerary */}
        <Card className="p-5 rounded-2xl border border-border/70 shadow-sm space-y-3 bg-card hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Itinerary</span>
            <div className="h-8 w-8 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400 flex items-center justify-center">
              <Compass className="h-4 w-4" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl font-bold text-foreground">{summary.itinerary_count || 0} Activities</div>
            <div className="text-xs text-muted-foreground mt-1">{summary.days_planned || 0} Days Planned</div>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${summary.itinerary_count > 0 ? 100 : 0}%` }} />
          </div>
        </Card>

        {/* Card 3: Booking */}
        <Card className="p-5 rounded-2xl border border-border/70 shadow-sm space-y-3 bg-card hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Booking</span>
            <div className="h-8 w-8 rounded-full bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400 flex items-center justify-center">
              <Ticket className="h-4 w-4" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl font-bold text-foreground">
              {summary.booking_completed || 0}/{summary.booking_count || 0} Completed
            </div>
            <div className="text-xs text-muted-foreground mt-1">Status Tiket &amp; Hotel</div>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div className="bg-purple-500 h-2 rounded-full transition-all" style={{ width: `${bookingPct}%` }} />
          </div>
        </Card>

        {/* Card 4: Checklist */}
        <Card className="p-5 rounded-2xl border border-border/70 shadow-sm space-y-3 bg-card hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Checklist</span>
            <div className="h-8 w-8 rounded-full bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400 flex items-center justify-center">
              <CheckSquare className="h-4 w-4" />
            </div>
          </div>
          <div>
            <div className="font-serif text-2xl font-bold text-foreground">
              {summary.checklist_completed || 0}/{summary.checklist_count || 0} Completed
            </div>
            <div className="text-xs text-muted-foreground mt-1">Persiapan Keberangkatan</div>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${checklistPct}%` }} />
          </div>
        </Card>
      </div>

      {/* MODULE TABS NAVIGATION */}
      <Tabs
        value={currentTab === "honeymoon" ? "overview" : currentTab}
        onValueChange={(val) => navigate(`/honeymoon/${val}`)}
        className="w-full space-y-6"
      >
        <TabsList className="bg-card border border-border/60 p-1.5 rounded-2xl w-full justify-start overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-nowrap md:flex-wrap">
          <TabsTrigger value="overview" className="rounded-xl px-4 py-2 text-xs font-medium">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="budget" className="rounded-xl px-4 py-2 text-xs font-medium">
            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Budget
          </TabsTrigger>
          <TabsTrigger value="itinerary" className="rounded-xl px-4 py-2 text-xs font-medium">
            <Compass className="h-3.5 w-3.5 mr-1.5" /> Itinerary
          </TabsTrigger>
          <TabsTrigger value="booking" className="rounded-xl px-4 py-2 text-xs font-medium">
            <Ticket className="h-3.5 w-3.5 mr-1.5" /> Booking
          </TabsTrigger>
          <TabsTrigger value="checklist" className="rounded-xl px-4 py-2 text-xs font-medium">
            <CheckSquare className="h-3.5 w-3.5 mr-1.5" /> Checklist
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Itinerary Preview */}
            <Card className="p-6 rounded-3xl border border-border/70 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg font-bold flex items-center gap-2">
                  <Compass className="h-5 w-5 text-primary" /> Rencana Itinerary
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate("/honeymoon/itinerary")} className="text-xs text-primary">
                  Lihat Semua <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>

              {itineraryData.items && itineraryData.items.length > 0 ? (
                <div className="space-y-3">
                  {itineraryData.items.slice(0, 4).map((item) => (
                    <div key={item.itinerary_id} className="p-3 rounded-2xl bg-muted/40 border border-border/40 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-primary font-bold">{item.time}</div>
                        <div>
                          <p className="font-semibold text-foreground">{item.title}</p>
                          <p className="text-[11px] text-muted-foreground">{item.location || item.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground text-xs border border-dashed rounded-2xl">
                  Belum ada aktivitas diatur. Klik tombol Itinerary untuk menambahkan.
                </div>
              )}
            </Card>

            {/* Quick Booking Preview */}
            <Card className="p-6 rounded-3xl border border-border/70 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg font-bold flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-purple-600" /> Status Booking Tiket &amp; Hotel
                </h3>
                <Button variant="ghost" size="sm" onClick={() => navigate("/honeymoon/booking")} className="text-xs text-primary">
                  Lihat Semua <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>

              {bookingData.bookings && bookingData.bookings.length > 0 ? (
                <div className="space-y-3">
                  {bookingData.bookings.slice(0, 4).map((item) => (
                    <div key={item.booking_id} className="p-3 rounded-2xl bg-muted/40 border border-border/40 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-[10px] uppercase font-bold">
                          {item.type}
                        </Badge>
                        <span className="font-medium text-foreground">{item.name}</span>
                      </div>
                      <Badge className={item.status === "Completed" || item.status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                        {item.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground text-xs border border-dashed rounded-2xl">
                  Belum ada booking tersimpan.
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* MODULE 1: BUDGET */}
        <TabsContent value="budget" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold">Honeymoon Budget</h2>
              <p className="text-xs text-muted-foreground">Kelola anggaran dan realisasi biaya bulan madu Anda.</p>
            </div>
            <Button
              onClick={() => {
                setEditingBudgetItem(null);
                setBudgetForm({ category: "Hotel", name: "", planned_amount: 0, actual_amount: 0, description: "", date: "" });
                setBudgetModalOpen(true);
              }}
              className="rounded-full h-10 px-5 text-xs font-semibold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Pengeluaran
            </Button>
          </div>

          {/* Kategori Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {(budgetData.categories || []).map((cat) => (
              <Card key={cat.category} className="p-4 rounded-2xl border border-border/70 space-y-2 bg-card">
                <span className="text-xs font-bold text-muted-foreground uppercase">{cat.category}</span>
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-muted-foreground">Budget: {idr(cat.planned)}</span>
                  <span className="text-xs font-semibold text-foreground">Actual: {idr(cat.actual)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full ${cat.actual > cat.planned ? "bg-destructive" : "bg-emerald-500"}`}
                    style={{ width: `${cat.planned > 0 ? Math.min(100, Math.round((cat.actual / cat.planned) * 100)) : 0}%` }}
                  />
                </div>
              </Card>
            ))}
          </div>

          {/* Table Pengeluaran */}
          <Card className="rounded-3xl border border-border/70 overflow-hidden shadow-xs">
            <div className="p-4 md:p-6 border-b border-border/60">
              <h3 className="font-serif text-lg font-bold">Daftar Transaksi Honeymoon</h3>
            </div>
            {budgetData.items && budgetData.items.length > 0 ? (
              <div className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted/60 text-muted-foreground uppercase text-[10px] tracking-wider font-semibold border-b border-border/60">
                    <tr>
                      <th className="px-4 py-3">Nama Transaksi</th>
                      <th className="px-4 py-3">Kategori</th>
                      <th className="px-4 py-3">Tanggal</th>
                      <th className="px-4 py-3 text-right">Planned</th>
                      <th className="px-4 py-3 text-right">Actual</th>
                      <th className="px-4 py-3 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {budgetData.items.map((item) => (
                      <tr key={item.budget_id} className="hover:bg-muted/30">
                        <td className="px-4 py-3 font-semibold text-foreground">{item.name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-[10px]">
                            {item.category}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{item.date || "—"}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{idr(item.planned_amount)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-foreground">{idr(item.actual_amount)}</td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditingBudgetItem(item);
                              setBudgetForm(item);
                              setBudgetModalOpen(true);
                            }}
                            className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteBudget(item.budget_id)}
                            className="h-7 w-7 rounded-lg text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-10 text-center text-muted-foreground text-xs">Belum ada rincian transaksi pengeluaran.</div>
            )}
          </Card>
        </TabsContent>

        {/* MODULE 2: ITINERARY */}
        <TabsContent value="itinerary" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold">Itinerary Planner</h2>
              <p className="text-xs text-muted-foreground">Susun urutan rencana aktivitas harian perjalanan Anda.</p>
            </div>
            <Button
              onClick={() => {
                setEditingItineraryItem(null);
                setItineraryForm({ date: trip.start_date || "", time: "09:00", title: "", location: "", description: "", image: "" });
                setItineraryModalOpen(true);
              }}
              className="rounded-full h-10 px-5 text-xs font-semibold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Aktivitas
            </Button>
          </div>

          {Object.keys(groupedItinerary).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(groupedItinerary).map(([dateStr, items], idx) => (
                <Card key={dateStr} className="p-6 rounded-3xl border border-border/70 space-y-4 bg-card">
                  <div className="flex items-center gap-2 border-b border-border/60 pb-3">
                    <Badge className="bg-primary text-white font-bold text-xs rounded-full">DAY {idx + 1}</Badge>
                    <span className="font-serif font-bold text-base text-foreground">{dateStr}</span>
                  </div>

                  <div className="relative pl-6 space-y-4 border-l-2 border-primary/30">
                    {items.map((item) => (
                      <div key={item.itinerary_id} className="relative group">
                        <div className="absolute -left-[31px] top-1 h-3.5 w-3.5 rounded-full bg-primary ring-4 ring-background" />
                        <div className="p-4 rounded-2xl bg-muted/30 border border-border/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:shadow-xs transition-all">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-primary">{item.time}</span>
                              <h4 className="font-serif text-base font-bold text-foreground">{item.title}</h4>
                            </div>
                            {item.location && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5 text-amber-500" /> {item.location}
                              </p>
                            )}
                            {item.description && <p className="text-xs text-muted-foreground/80 mt-1 leading-relaxed">{item.description}</p>}
                          </div>

                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                setEditingItineraryItem(item);
                                setItineraryForm(item);
                                setItineraryModalOpen(true);
                              }}
                              className="h-8 w-8 rounded-lg"
                            >
                              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteItinerary(item.itinerary_id)}
                              className="h-8 w-8 rounded-lg text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-10 rounded-3xl border border-dashed border-border text-center space-y-3">
              <Compass className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="text-sm font-semibold text-foreground">Belum ada aktivitas itinerary.</p>
              <p className="text-xs text-muted-foreground">Klik tombol Tambah Aktivitas untuk memulai menyusun acara per hari.</p>
            </Card>
          )}
        </TabsContent>

        {/* MODULE 3: BOOKING */}
        <TabsContent value="booking" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold">Booking Management</h2>
              <p className="text-xs text-muted-foreground">Simpan voucher hotel, tiket pesawat, dan bukti pemesanan.</p>
            </div>
            <Button
              onClick={() => {
                setEditingBookingItem(null);
                setBookingForm({ type: "Hotel", name: "", booking_date: "", status: "Pending", document: "" });
                setBookingModalOpen(true);
              }}
              className="rounded-full h-10 px-5 text-xs font-semibold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Booking
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bookingData.bookings && bookingData.bookings.length > 0 ? (
              bookingData.bookings.map((item) => (
                <Card key={item.booking_id} className="p-5 rounded-3xl border border-border/70 space-y-3 bg-card flex flex-col justify-between">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-300 flex items-center justify-center font-bold">
                        <Ticket className="h-5 w-5" />
                      </div>
                      <div>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold mb-1">
                          {item.type}
                        </Badge>
                        <h4 className="font-serif text-base font-bold text-foreground">{item.name}</h4>
                        <p className="text-xs text-muted-foreground">{item.booking_date || "—"}</p>
                      </div>
                    </div>
                    <Badge className={item.status === "Completed" || item.status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                      {item.status}
                    </Badge>
                  </div>

                  {item.document && (
                    <div className="text-xs p-2.5 rounded-xl bg-muted/40 border border-border/40 flex items-center justify-between">
                      <span className="truncate text-muted-foreground max-w-[200px]">{item.document}</span>
                      <a href={item.document} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold flex items-center gap-1 hover:underline">
                        Lihat Tiket <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingBookingItem(item);
                        setBookingForm(item);
                        setBookingModalOpen(true);
                      }}
                      className="text-xs text-muted-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteBooking(item.booking_id)}
                      className="text-xs text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus
                    </Button>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="col-span-2 p-10 rounded-3xl border border-dashed border-border text-center space-y-3">
                <Ticket className="h-10 w-10 mx-auto text-muted-foreground/40" />
                <p className="text-sm font-semibold text-foreground">Belum ada data booking tersimpan.</p>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* MODULE 4: CHECKLIST */}
        <TabsContent value="checklist" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-xl md:text-2xl font-bold">Honeymoon Checklist</h2>
              <p className="text-xs text-muted-foreground">Persiapan sebelum keberangkatan &amp; perlengkapan selama perjalanan.</p>
            </div>
            <Button
              onClick={() => {
                setEditingChecklistItem(null);
                setChecklistForm({ title: "", category: "Before Departure" });
                setChecklistModalOpen(true);
              }}
              className="rounded-full h-10 px-5 text-xs font-semibold"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Checklist
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CHECKLIST_CATEGORIES.map((cat) => {
              const catItems = (checklistData.items || []).filter((i) => i.category === cat);
              return (
                <Card key={cat} className="p-6 rounded-3xl border border-border/70 space-y-4 bg-card">
                  <div className="flex items-center justify-between border-b border-border/60 pb-3">
                    <h3 className="font-serif text-base font-bold text-foreground flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-amber-500" /> {cat}
                    </h3>
                    <Badge variant="outline" className="text-[10px]">
                      {catItems.filter((i) => i.completed).length}/{catItems.length}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {catItems.map((item) => (
                      <div
                        key={item.checklist_id}
                        className="p-3 rounded-2xl bg-muted/30 border border-border/40 flex items-center justify-between gap-3 hover:bg-muted/50 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleChecklist(item)}
                          className="flex items-center gap-3 text-left flex-1"
                        >
                          <div
                            className={`h-5 w-5 rounded-md border flex items-center justify-center transition-all ${
                              item.completed ? "bg-emerald-500 border-emerald-500 text-white" : "border-muted-foreground/40 bg-background"
                            }`}
                          >
                            {item.completed && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                          </div>
                          <span className={`text-xs font-medium ${item.completed ? "line-through text-muted-foreground" : "text-foreground"}`}>
                            {item.title}
                          </span>
                        </button>

                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteChecklist(item.checklist_id)}
                          className="h-6 w-6 rounded-lg text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* MODAL: EDIT TRIP */}
      <Dialog open={tripModalOpen} onOpenChange={setTripModalOpen}>
        <DialogContent className="max-w-md bg-card p-6 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Edit Informasi Honeymoon</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTrip} className="space-y-4 py-2 text-xs">
            <div>
              <Label className="text-xs font-semibold">Judul Perjalanan</Label>
              <Input value={tripForm.title || ""} onChange={(e) => setTripForm({ ...tripForm, title: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Destinasi</Label>
              <Input value={tripForm.destination || ""} onChange={(e) => setTripForm({ ...tripForm, destination: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Tanggal Mulai</Label>
                <Input type="date" value={tripForm.start_date || ""} onChange={(e) => setTripForm({ ...tripForm, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Tanggal Selesai</Label>
                <Input type="date" value={tripForm.end_date || ""} onChange={(e) => setTripForm({ ...tripForm, end_date: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Durasi</Label>
                <Input placeholder="5 Days 4 Nights" value={tripForm.duration || ""} onChange={(e) => setTripForm({ ...tripForm, duration: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Status Trip</Label>
                <select value={tripForm.status || "Planning"} onChange={(e) => setTripForm({ ...tripForm, status: e.target.value })} className="mt-1 w-full border border-input rounded-md h-9 text-xs px-2 bg-background">
                  {TRIP_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Total Budget (Rp)</Label>
              <Input type="number" value={tripForm.budget || 0} onChange={(e) => setTripForm({ ...tripForm, budget: parseFloat(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold block mb-1.5">Foto Cover Honeymoon</Label>
              {trip.cover_image && (
                <div className="mb-2 h-24 w-full rounded-xl overflow-hidden border border-border bg-muted relative">
                  <img
                    src={formatMediaUrl(trip.cover_image)}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="cursor-pointer flex-1 inline-flex items-center justify-center gap-2 h-9 px-4 rounded-xl border border-input bg-background hover:bg-accent text-xs font-medium transition-colors">
                  {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Camera className="h-4 w-4 text-primary" />}
                  <span>{uploadingCover ? "Mengunggah..." : "Unggah File Gambar..."}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleUploadCoverFile} disabled={uploadingCover} />
                </label>
                {trip.cover_image && (
                  <Button type="button" variant="outline" size="sm" onClick={handleDeleteCover} className="h-9 text-destructive hover:text-destructive shrink-0">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Hapus
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Format gambar JPG, PNG, atau WebP (Maksimal 5 MB).</p>
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full rounded-full">Simpan Perubahan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: BUDGET FORM */}
      <Dialog open={budgetModalOpen} onOpenChange={setBudgetModalOpen}>
        <DialogContent className="max-w-md bg-card p-6 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editingBudgetItem ? "Edit Pengeluaran" : "Tambah Pengeluaran Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBudget} className="space-y-4 py-2 text-xs">
            <div>
              <Label className="text-xs font-semibold">Kategori *</Label>
              <select value={budgetForm.category} onChange={(e) => setBudgetForm({ ...budgetForm, category: e.target.value })} className="mt-1 w-full border border-input rounded-md h-9 text-xs px-2 bg-background">
                {BUDGET_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Nama Transaksi *</Label>
              <Input value={budgetForm.name} onChange={(e) => setBudgetForm({ ...budgetForm, name: e.target.value })} required className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Planned Amount (Rp)</Label>
                <Input type="number" value={budgetForm.planned_amount} onChange={(e) => setBudgetForm({ ...budgetForm, planned_amount: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Actual Expense (Rp)</Label>
                <Input type="number" value={budgetForm.actual_amount} onChange={(e) => setBudgetForm({ ...budgetForm, actual_amount: parseFloat(e.target.value) || 0 })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Tanggal</Label>
              <Input type="date" value={budgetForm.date || ""} onChange={(e) => setBudgetForm({ ...budgetForm, date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Catatan</Label>
              <Input value={budgetForm.description || ""} onChange={(e) => setBudgetForm({ ...budgetForm, description: e.target.value })} className="mt-1" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full rounded-full">Simpan Budget</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: ITINERARY FORM */}
      <Dialog open={itineraryModalOpen} onOpenChange={setItineraryModalOpen}>
        <DialogContent className="max-w-md bg-card p-6 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editingItineraryItem ? "Edit Aktivitas Itinerary" : "Tambah Aktivitas Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveItinerary} className="space-y-4 py-2 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Tanggal</Label>
                <Input type="date" value={itineraryForm.date || ""} onChange={(e) => setItineraryForm({ ...itineraryForm, date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Jam</Label>
                <Input placeholder="09:00" value={itineraryForm.time || ""} onChange={(e) => setItineraryForm({ ...itineraryForm, time: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Judul Aktivitas *</Label>
              <Input value={itineraryForm.title} onChange={(e) => setItineraryForm({ ...itineraryForm, title: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Lokasi</Label>
              <Input value={itineraryForm.location || ""} onChange={(e) => setItineraryForm({ ...itineraryForm, location: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Deskripsi / Detail</Label>
              <Textarea value={itineraryForm.description || ""} onChange={(e) => setItineraryForm({ ...itineraryForm, description: e.target.value })} className="mt-1 h-20" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full rounded-full">Simpan Itinerary</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: BOOKING FORM */}
      <Dialog open={bookingModalOpen} onOpenChange={setBookingModalOpen}>
        <DialogContent className="max-w-md bg-card p-6 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editingBookingItem ? "Edit Booking" : "Tambah Booking Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBooking} className="space-y-4 py-2 text-xs">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Tipe Booking *</Label>
                <select value={bookingForm.type} onChange={(e) => setBookingForm({ ...bookingForm, type: e.target.value })} className="mt-1 w-full border border-input rounded-md h-9 text-xs px-2 bg-background">
                  {BOOKING_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Status *</Label>
                <select value={bookingForm.status} onChange={(e) => setBookingForm({ ...bookingForm, status: e.target.value })} className="mt-1 w-full border border-input rounded-md h-9 text-xs px-2 bg-background">
                  {BOOKING_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Nama Booking *</Label>
              <Input value={bookingForm.name} onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })} required className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Tanggal Pemesanan / Check-in</Label>
              <Input type="date" value={bookingForm.booking_date || ""} onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Link Tiket / Voucher / No. Referensi</Label>
              <Input value={bookingForm.document || ""} onChange={(e) => setBookingForm({ ...bookingForm, document: e.target.value })} className="mt-1" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full rounded-full">Simpan Booking</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL: CHECKLIST FORM */}
      <Dialog open={checklistModalOpen} onOpenChange={setChecklistModalOpen}>
        <DialogContent className="max-w-md bg-card p-6 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editingChecklistItem ? "Edit Item Checklist" : "Tambah Item Checklist"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveChecklist} className="space-y-4 py-2 text-xs">
            <div>
              <Label className="text-xs font-semibold">Kategori *</Label>
              <select value={checklistForm.category} onChange={(e) => setChecklistForm({ ...checklistForm, category: e.target.value })} className="mt-1 w-full border border-input rounded-md h-9 text-xs px-2 bg-background">
                {CHECKLIST_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Judul Item Checklist *</Label>
              <Input value={checklistForm.title} onChange={(e) => setChecklistForm({ ...checklistForm, title: e.target.value })} required className="mt-1" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="w-full rounded-full">Simpan Checklist</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
